import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import AndroidIcon from '@mui/icons-material/Android';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import IosShareIcon from '@mui/icons-material/IosShare';
import SettingsIcon from '@mui/icons-material/Settings';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

import { NAV_BG, AMBER, AMBER_DARK } from '../theme';

function StepList({ steps }) {
  return (
    <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
      {steps.map((step, i) => (
        <Box
          component="li"
          key={i}
          sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.88rem', color: '#374151', mb: 0.75, lineHeight: 1.55 }}
        >
          {step}
        </Box>
      ))}
    </Box>
  );
}

function Note({ children }) {
  return (
    <Box sx={{ mt: 1.5, px: 1.5, py: 1, background: 'rgba(245,158,11,0.08)', borderRadius: 1.5, borderLeft: `3px solid ${AMBER}` }}>
      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#92400e', lineHeight: 1.5 }}>
        {children}
      </Typography>
    </Box>
  );
}

function FaqItem({ question, icon, accentColor, children }) {
  return (
    <Accordion
      elevation={0}
      sx={{
        mb: 1.5,
        border: '1px solid #e2e8f0',
        borderRadius: '10px !important',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { borderColor: `${accentColor || NAV_BG}33` },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: accentColor || NAV_BG }} />}
        sx={{ px: 2.5, '& .MuiAccordionSummary-content': { my: 1.25 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ color: accentColor || NAV_BG, display: 'flex', flexShrink: 0 }}>{icon}</Box>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.9rem', color: NAV_BG, lineHeight: 1.4 }}>
            {question}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

function SubTitle({ icon, label }) {
  return (
    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '0.85rem', color: NAV_BG, mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {icon} {label}
    </Typography>
  );
}

export default function ParentHelpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.4rem', color: NAV_BG, mb: 0.5 }}>
        {t('parent_help_page.title')}
      </Typography>
      <Typography sx={{ fontFamily: '"Outfit", sans-serif', color: '#64748b', mb: 3 }}>
        {t('parent_help_page.subtitle')}
      </Typography>

      <FaqItem question={t('parent_help_page.q_ios_notif')} icon={<PhoneIphoneIcon sx={{ fontSize: 20 }} />} accentColor={NAV_BG}>
        <StepList steps={[
          t('parent_help_page.ios_step1'),
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {t('parent_help_page.ios_step2')}&nbsp;<IosShareIcon sx={{ fontSize: 16, color: '#2563eb', verticalAlign: 'middle' }} />
          </Box>,
          t('parent_help_page.ios_step3'),
          t('parent_help_page.ios_step4'),
          t('parent_help_page.ios_step5'),
          t('parent_help_page.ios_step6'),
        ]} />
        <Note>{t('parent_help_page.ios_note')}</Note>
      </FaqItem>

      <FaqItem question={t('parent_help_page.q_android_notif')} icon={<AndroidIcon sx={{ fontSize: 20 }} />} accentColor="#166534">
        <StepList steps={[
          t('parent_help_page.android_step1'),
          t('parent_help_page.android_step2'),
          t('parent_help_page.android_step3'),
          t('parent_help_page.android_step4'),
        ]} />
      </FaqItem>

      <FaqItem question={t('parent_help_page.q_desktop_notif')} icon={<DesktopWindowsIcon sx={{ fontSize: 20 }} />} accentColor="#1e40af">
        <StepList steps={[
          t('parent_help_page.desktop_step1'),
          t('parent_help_page.desktop_step2'),
          t('parent_help_page.desktop_step3'),
        ]} />
      </FaqItem>

      <FaqItem question={t('parent_help_page.q_notif_preview')} icon={<NotificationsActiveIcon sx={{ fontSize: 20 }} />} accentColor={AMBER}>
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.88rem', color: '#374151', mb: 1.5, lineHeight: 1.55 }}>
          {t('parent_help_page.notif_preview_intro')}
        </Typography>
        <SubTitle icon={<PhoneIphoneIcon sx={{ fontSize: 16 }} />} label={t('parent_help_page.ios_preview_title')} />
        <StepList steps={[
          t('parent_help_page.ios_preview_step1'),
          t('parent_help_page.ios_preview_step2'),
          t('parent_help_page.ios_preview_step3'),
        ]} />
        <SubTitle icon={<AndroidIcon sx={{ fontSize: 16 }} />} label={t('parent_help_page.android_preview_title')} />
        <StepList steps={[
          t('parent_help_page.android_preview_step1'),
          t('parent_help_page.android_preview_step2'),
          t('parent_help_page.android_preview_step3'),
        ]} />
      </FaqItem>

      <FaqItem question={t('parent_help_page.q_ios_expand')} icon={<UnfoldMoreIcon sx={{ fontSize: 20 }} />} accentColor={NAV_BG}>
        <StepList steps={[
          t('parent_help_page.ios_expand_step1'),
          t('parent_help_page.ios_expand_step2'),
          t('parent_help_page.ios_expand_step3'),
          t('parent_help_page.ios_expand_step4'),
        ]} />
      </FaqItem>

      <FaqItem question={t('parent_help_page.q_android_expand')} icon={<UnfoldMoreIcon sx={{ fontSize: 20 }} />} accentColor="#166534">
        <StepList steps={[
          t('parent_help_page.android_expand_step1'),
          t('parent_help_page.android_expand_step2'),
          t('parent_help_page.android_expand_step3'),
        ]} />
      </FaqItem>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          disableElevation
          startIcon={<SettingsIcon />}
          onClick={() => navigate('/parent/settings', { replace: true })}
          sx={{
            background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
            color: NAV_BG,
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 1.5,
            px: 3,
          }}
        >
          {t('parent_help_page.go_to_settings')}
        </Button>
      </Box>
    </Box>
  );
}
